import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Box } from "@mui/material";
import * as React from "react";
import { ToolButton } from "ui/Scripts/source/components/framework/Button/ToolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { EventTypeNode, IEventTypeNode } from "source/models/eventType/eventTypeNode";
import { EventTypeNodes } from "source/models/eventType/eventTypeNodes";
import * as eventService from "source/models/services/eventService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { ListHeaderLine } from "source/components/framework/List/ListLine";
import { EventTypeEditDialog } from "../EventTypeEditDialog";
import { EventTypeTreeNode } from "../EventTypeTreeNode";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { AddToolButton } from "source/components/framework/Button/AddToolButton";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IEventTypeTreeProp {
    classes?: string;
    companyId: string;
    category: number;
    selectedId?: string;
    eventTypeNodes: EventTypeNodes;
    getEventTypes: (category: number) => Promise<EventTypeNodes>;
}

export interface IEventTypeTreeState {
    // Tree
    items: IEventTypeNode[];
    selectedId: string;
    selectedCanBeRemoved: boolean;

    // Dialog
    showEditDialog: boolean;
    editDialogId: string;
    editDialogParentId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export class EventTypeTree extends React.Component<IEventTypeTreeProp, IEventTypeTreeState> {
    getStateFromEventTypeNodes = (state: IEventTypeTreeState, eventTypeNodes: EventTypeNodes, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        let selectedItem = EventTypeNode.getEventTypeNodeById(eventTypeNodes.items, state.selectedId);
        let selectedId = selectedItem ? selectedItem.id : "";
        const list = EventTypeNode.getAsList(state.items);
        let index = list.findIndex(i => i.id === selectedId);
        if (selectedItem && moveNext && index < list.length - 1) {
            index = index + 1;
            selectedItem = list[index];
            selectedId = selectedItem.id;
        }
        return {
            items: eventTypeNodes.items,
            selectedId,
            selectedCanBeRemoved: selectedItem ? selectedItem.children.length < 1 : false,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? index < list.length - 1 : state.editDialogHasNext,
            editDialogParentId: ""
        };
    };

    constructor(props: IEventTypeTreeProp) {
        super(props);
        const selectedId = props.selectedId
            ? props.selectedId
            : (props.eventTypeNodes.items.length > 0
                ? props.eventTypeNodes.items[0].id
                : "");
        const state = {
            items: [],
            selectedId,
            selectedCanBeRemoved: false,
            showEditDialog: false,
            editDialogId: "",
            editDialogParentId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false
        };
        const newState = this.getStateFromEventTypeNodes(state, props.eventTypeNodes);
        this.state = {
            ...state,
            ...newState
        };
    }

    searchItems = async (moveNext: boolean = false, setEditDialogData: boolean = true) => {
        const props = this.props;
        const result = await props.getEventTypes(props.category);
        if (!result) return;
        this.setState(this.getStateFromEventTypeNodes(this.state, result, moveNext, setEditDialogData));
    };

    refreshTree = async (setDialogData: boolean = true) => {
        await this.searchItems(false, setDialogData);
    };

    componentDidUpdate(prevProps: IEventTypeTreeProp, prevState: IEventTypeTreeState) {
        const props = this.props;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId && prevProps.category === props.category) return;
        this.searchItems();
    }

    handleNodeClick = (id: string) => {
        if (!id) return;
        const selectedItem = EventTypeNode.getEventTypeNodeById(this.state.items, id);
        if (!selectedItem) return;
        this.setState({
            selectedId: selectedItem ? selectedItem.id : "",
            selectedCanBeRemoved: selectedItem ? selectedItem.children.length < 1 : false,
        });
    };

    handleSetCollapsed = (id: string, value: boolean) => {
        const state = this.state;
        if (!id) return;
        const items = state.items.map(i => new EventTypeNode(i));
        const selectedItem = EventTypeNode.getEventTypeNodeById(items, id);
        if (!selectedItem) return;
        selectedItem.collapsed = value;
        this.setState({
            items,
            selectedId: selectedItem.id,
            selectedCanBeRemoved: selectedItem.children.length < 1,
        });
    };

    remove = async (id: string, code: string): Promise<ApiSuccess> => {
        const props = this.props;
        if (!id) return;
        // Call server
        const result = await AppUtils.callService(() => eventService.removeEventType(props.companyId, props.category, id, code));
        if (!result) return null;
        this.refreshTree();
        return result;
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (!state.selectedId) return;
        const selectedItem = EventTypeNode.getEventTypeNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        const confirmationText = props.category < 0.5
            ? Translations.DoYouReallyWantToRemoveEventTypeParameter
            : (props.category < 1.5
                ? Translations.DoYouReallyWantToRemoveEventTypeBParameter
                : Translations.DoYouReallyWantToRemoveEventTypeCParameter);
        if (await AppUtils.showConfirmationDialog(String.format(confirmationText, selectedItem.code), false) === ConfirmationDialogResult.Yes) {
            await this.remove(selectedItem.id, selectedItem.code);
        }
    };

    handleShowEditDialog = (eventTypeId: string, eventTypeParentId: string, selectedCanBeRemoved: boolean) => {
        const state = this.state;
        const isNew = eventTypeId === Base.emptyGuid;
        const list = EventTypeNode.getAsList(state.items);
        const index = isNew ? 0 : list.findIndex(i => i.id === eventTypeId);
        if (index < 0) return;
        this.setState({
            showEditDialog: true,
            selectedId: eventTypeId,
            selectedCanBeRemoved: isNew ? state.selectedCanBeRemoved : selectedCanBeRemoved,
            editDialogId: eventTypeId,
            editDialogParentId: isNew ? eventTypeParentId : "",
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew ? false : index < list.length
        });
    };

    handleEditDialogOk = () => {
        this.refreshTree(false);
    };

    handleEditDialogCancel = (refreshTree: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!refreshTree) return;
        this.refreshTree(false);
    };

    handleAdd = () => {
        this.handleShowEditDialog(Base.emptyGuid, "", null);
    };

    handleAddSub = () => {
        const state = this.state;
        if (!state.selectedId) return;
        const selectedItem = EventTypeNode.getEventTypeNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        this.handleShowEditDialog(Base.emptyGuid, selectedItem.id, null);
    };
    
    handleOpen = (id: string) => {
        const state = this.state;
        if (!id) return;
        const selectedItem = EventTypeNode.getEventTypeNodeById(state.items, id);
        if (!selectedItem) return;
        this.handleShowEditDialog(id, "", selectedItem.children.length < 1);
    };

    setEditDialogItemByIndex = (index: number, list: IEventTypeNode[]) => {
        if (index < 0 || index >= list.length) return;
        this.setState({
            selectedId: list[index].id,
            editDialogId: list[index].id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < list.length - 1,
            selectedCanBeRemoved: list[index].children.length < 1
        });
    };

    handleMoveToPrevious = () => {
        const state = this.state;
        if (!state.selectedId) return;
        const selectedItem = EventTypeNode.getEventTypeNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        const list = EventTypeNode.getAsList(state.items);
        const index = list.findIndex(i => i.id === selectedItem.id);
        if (index < 0) return;
        this.setEditDialogItemByIndex(index - 1, list);
    };

    handleMoveToNext = () => {
        const state = this.state;
        if (!state.selectedId) return;
        const selectedItem = EventTypeNode.getEventTypeNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        const list = EventTypeNode.getAsList(state.items);
        const index = list.findIndex(i => i.id === selectedItem.id);
        if (index < 0) return;
        this.setEditDialogItemByIndex(index + 1, list);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const items = state.items;
        return (
            <>
                <DialogSettingsCard
                    fitToContainerHeight
                    title={props.category < 0.5
                        ? (props.companyId ? Translations.EventTypes : Translations.DefaultEventTypes)
                        : (props.category < 1.5
                            ? Translations.EventTypeBs
                            : Translations.EventTypeCs)}
                    info={props.companyId ? Translations.EventModuleInfoEventTypes : undefined}
                    cardContent={<>
                        <ActionContainer
                            leftItems={
                                <></>
                            }
                            rightItems={
                                <Box sx={{ marginLeft: ".5rem", display: "flex" }}>
                                    <AddToolButton
                                        tooltip={props.category < 0.5
                                            ? (props.companyId ? Translations.AddEventType : Translations.AddDefaultEventType)
                                            : (props.category < 1.5
                                                ? Translations.AddEventTypeB
                                                : Translations.AddEventTypeC)}
                                        onClick={this.handleAdd}
                                    />
                                    <ToolButton
                                        variant="icon"
                                        tooltip={props.category < 0.5
                                            ? (props.companyId ? Translations.AddChildEventType : Translations.AddChildDefaultEventType)
                                            : (props.category < 1.5
                                                ? Translations.AddChildEventTypeB
                                                : Translations.AddChildEventTypeC)}
                                        disabled={!state.selectedId}
                                        icon={<PlaylistAddIcon />}
                                        onClick={this.handleAddSub}
                                    />
                                    <RemoveToolButton
                                        disabled={!state.selectedId || !state.selectedCanBeRemoved}
                                        tooltip={props.category < 0.5
                                            ? (props.companyId ? Translations.RemoveEventType : Translations.RemoveDefaultEventType)
                                            : (props.category < 1.5
                                                ? Translations.RemoveEventTypeB
                                                : Translations.RemoveEventTypeC)}
                                        onClick={this.handleRemove}
                                    />
                                </Box>
                            }
                        />
                    </>}
                    content={
                        <VerticalContainer
                            before={
                                <ListHeaderLine
                                    columns={[
                                        { size: 5, column: "name", content: Translations.Name },
                                        { size: 5, column: "permission", content: Translations.Description },
                                        { size: 2, column: "permission", content: Translations.InUse },
                                    ]}
                                />
                            }
                            main={
                                <Box sx={{
                                    height: "100%",
                                    overflowX: "hidden",
                                    overflowY: "auto",
                                }}>
                                    {items.map((item) =>
                                        <EventTypeTreeNode
                                            key={item.id}
                                            item={item}
                                            level={1}
                                            selectedId={state.selectedId}
                                            onClick={this.handleNodeClick}
                                            onDoubleClick={this.handleOpen}
                                            onSetCollapsed={this.handleSetCollapsed}
                                        />
                                    )}
                                </Box>
                            }
                        />
                    }
                />
                {state.showEditDialog &&
                    <EventTypeEditDialog
                        id={state.editDialogId}
                        companyId={props.companyId}
                        parentId={state.editDialogParentId}
                        category={props.category}
                        canBeRemoved={state.selectedCanBeRemoved}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onPrevious={this.handleMoveToPrevious}
                        onNext={this.handleMoveToNext}
                        onOk={this.handleEditDialogOk}
                        onClose={this.handleEditDialogCancel}
                        onRemove={this.remove}
                        onRefreshList={() => this.refreshTree(false)}
                    />
                }
            </>
        );
    }
}